import { Field, Label, ErrorMessage } from "V2.0/common/fieldset";
import { ListboxWithSections } from "V2.0/components/ListBoxGrouped";
import { Badge } from "V2.0/common/badge";
import { FaCrown, FaStar } from "react-icons/fa";
// Replaced: the Jarvis Labs (India deployed) models were never imported, so they could not be
// picked as test models even for assistants whose data residency resolves to India
// import {
//   basicModels,
//   proModels,
//   premiumModels,
// } from "V2.0/HomePages/Assistants/ModelSettings/GPTResponseModelCategorized/gptModels";
import {
  basicModels,
  proModels,
  premiumModels,
  jarvisLabsBasicModels,
  jarvisLabsProModels,
  jarvisLabsPremiumModels,
} from "V2.0/HomePages/Assistants/ModelSettings/GPTResponseModelCategorized/gptModels";
import { getModelOptions } from "V2.0/HomePages/Assistants/ModelSettings/GPTResponseModelCategorized/GPTResponseModelSettings";
import { useMemo } from "react";
import { MultiListbox } from "V2.0/components/MultiListBox";

export default ModelsSelect;

const [
  basicModelOptions,
  proModelOptions,
  premiumModelOptions,
  premiumModelDisabledOptions,
] = [
  getModelOptions(basicModels),
  getModelOptions(proModels),
  getModelOptions(premiumModels),
  getModelOptions(premiumModels, {
    disabled: true,
  }),
];

const [
  jarvisLabsBasicOptions,
  jarvisLabsProOptions,
  jarvisLabsPremiumOptions,
  jarvisLabsPremiumDisabledOptions,
] = [
  getModelOptions(jarvisLabsBasicModels),
  getModelOptions(jarvisLabsProModels),
  getModelOptions(jarvisLabsPremiumModels),
  getModelOptions(jarvisLabsPremiumModels, {
    disabled: true,
  }),
];

// Jarvis Labs (India deployed) models are offered only when the assistant's audio data
// residency resolves to India — the same rule as the assistant's model dropdown. Otherwise
// only the ones already selected are kept, so a saved test still shows them and they can be
// removed.
function indiaDeployedOptions(options, showIndiaDeployedModels, selectedModels) {
  if (showIndiaDeployedModels) return options;
  const selected = new Set(selectedModels ?? []);
  return options.filter((option) => selected.has(option.value));
}

const basicModelGroup = {
  label: "Basic",
  sticky: true,
  options: basicModelOptions,
};

const proModelGroup = {
  labelNode: (
    <div>
      Pro{" "}
      <Badge color="orange">
        <FaStar />
      </Badge>
    </div>
  ),
  label: "Pro",
  sticky: true,
  options: proModelOptions,
};

const premiumModelGroup = {
  labelNode: (
    <div>
      Premium{" "}
      <Badge color="amber">
        <FaCrown />
      </Badge>
    </div>
  ),
  label: "Premium",
  sticky: true,
  options: premiumModelOptions,
};

const premiumModelDisabledGroup = {
  ...premiumModelGroup,
  options: premiumModelDisabledOptions,
};

function ModelsSelect(props) {
  const { label, value, error, onChange, enablePremiumOptions, showPremiumOptions } =
    props;

  const options = useMemo(() => {
    const premiumGroup = enablePremiumOptions
      ? premiumModelGroup
      : premiumModelDisabledGroup;
    return [
      basicModelGroup,
      proModelGroup,
      ...(showPremiumOptions ? [premiumGroup] : []),
    ];
  }, [enablePremiumOptions, showPremiumOptions]);

  return (
    <Field>
      <Label>{label}</Label>
      <ListboxWithSections
        as="div"
        className="my-4"
        options={options}
        value={value}
        defaultValue={value}
        onChange={onChange}
        invalid={!!error}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Field>
  );
}

export function ModelsSelectMultiple(props) {
  // Replaced: also takes showIndiaDeployedModels, so the Jarvis Labs models can be offered
  // const { value, onChange, error, enablePremiumOptions, showPremiumOptions, label } =
  //   props;
  const {
    value,
    onChange,
    error,
    enablePremiumOptions,
    showPremiumOptions,
    showIndiaDeployedModels,
    label,
  } = props;

  // Replaced: Jarvis Labs models are appended to each tier when the assistant resolves to India
  // const options = useMemo(() => {
  //   const premiumGroup = enablePremiumOptions
  //     ? premiumModelGroup
  //     : premiumModelDisabledGroup;
  //   return [
  //     basicModelGroup,
  //     proModelGroup,
  //     ...(showPremiumOptions ? [premiumGroup] : []),
  //   ];
  // }, [enablePremiumOptions, showPremiumOptions]);
  const options = useMemo(() => {
    const premiumGroup = enablePremiumOptions
      ? premiumModelGroup
      : premiumModelDisabledGroup;
    const jarvisLabsPremiumGroupOptions = enablePremiumOptions
      ? jarvisLabsPremiumOptions
      : jarvisLabsPremiumDisabledOptions;

    return [
      {
        ...basicModelGroup,
        options: [
          ...basicModelGroup.options,
          ...indiaDeployedOptions(
            jarvisLabsBasicOptions,
            showIndiaDeployedModels,
            value,
          ),
        ],
      },
      {
        ...proModelGroup,
        options: [
          ...proModelGroup.options,
          ...indiaDeployedOptions(
            jarvisLabsProOptions,
            showIndiaDeployedModels,
            value,
          ),
        ],
      },
      ...(showPremiumOptions
        ? [
            {
              ...premiumGroup,
              options: [
                ...premiumGroup.options,
                ...indiaDeployedOptions(
                  jarvisLabsPremiumGroupOptions,
                  showIndiaDeployedModels,
                  value,
                ),
              ],
            },
          ]
        : []),
    ];
  }, [enablePremiumOptions, showPremiumOptions, showIndiaDeployedModels, value]);

  return (
    <Field>
      <Label>{label}</Label>
      <MultiListbox
        className="my-4"
        placeholder="No Models Selected"
        options={options}
        value={value}
        onChange={onChange}
        invalid={!!error}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Field>
  );
}
